import { DateTime } from "luxon";
import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";

import { useSelector } from "../../../store";
import { useGetKeyPairQuery } from "../../../store/pomeriumApi";
import { issuerName, subjectName } from "../../../utils/certificates";
import { expiresSoon } from "../../../utils/date";

interface DetailViewDialogProps {
  open: boolean;
  onClose: () => void;
  keyPairId: string;
}

interface DataPointProps {
  title: string;
}

const DataPoint = (props: React.PropsWithChildren<DataPointProps>) => {
  return (
    <Grid container spacing={1} sx={{ m: 2 }}>
      <Grid size={4}>
        <Typography variant="h6">{props.title}</Typography>
      </Grid>
      <Grid size={8}>{props.children}</Grid>
    </Grid>
  );
};

const DetailViewDialog = ({
  open,
  onClose,
  keyPairId,
}: DetailViewDialogProps) => {
  const { organizationId } = useSelector((state) => state.current);
  const { data: cert, isLoading } = useGetKeyPairQuery(
    { organizationId, keyPairId },
    {
      skip: !organizationId || !keyPairId,
    },
  );

  if (!cert || isLoading) {
    return <></>;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Cert Details
          <IconButton
            color="primary"
            onClick={onClose}
            size="large"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Container disableGutters>
            <DataPoint title={"Serial"}>
              <Typography>
                {cert?.certificateInfo?.map((c) => c?.serial)?.join(", ")}
              </Typography>
            </DataPoint>
            <Divider />
            <DataPoint title={"Issuer"}>
              <List disablePadding>
                {cert?.certificateInfo
                  ?.map(issuerName)
                  ?.filter(Boolean)
                  ?.join(", ")}
              </List>
            </DataPoint>
            <Divider />
            <DataPoint title={"Subject"}>
              <List disablePadding>
                {cert?.certificateInfo
                  ?.map(subjectName)
                  ?.filter(Boolean)
                  ?.join(", ")}
              </List>
            </DataPoint>
            <Divider />
            <DataPoint title={"DNS Names"}>
              <List disablePadding>
                {cert?.certificateInfo
                  ?.flatMap((c) => c?.dnsNames || [])
                  ?.map((dns_name) => (
                    <ListItem disablePadding key={dns_name}>
                      <Typography>{dns_name}</Typography>
                    </ListItem>
                  ))}
              </List>
            </DataPoint>
            <Divider />
            <DataPoint title={"Expires"}>
              <Typography
                sx={{
                  color: expiresSoon(cert?.certificateInfo?.[0]?.notAfter)
                    ? "error.main"
                    : "success.main",
                }}
              >
                {cert?.certificateInfo?.[0]?.notAfter &&
                  DateTime.fromISO(
                    cert?.certificateInfo?.[0]?.notAfter,
                  ).toLocaleString()}
              </Typography>
            </DataPoint>
            <Divider />
            <DataPoint title={"Certificate Authority?"}>
              <Typography>
                {cert?.certificateInfo?.some((c) => c?.keyUsage?.certSign)
                  ? "True"
                  : "False"}
              </Typography>
            </DataPoint>
            <Divider />
            <DataPoint title={"Server?"}>
              <Typography>
                {cert?.certificateInfo?.some((c) => c?.extKeyUsage?.serverAuth)
                  ? "True"
                  : "False"}
              </Typography>
            </DataPoint>
            <Divider />
            <DataPoint title={"Client?"}>
              <Typography>
                {cert?.certificateInfo?.some((c) => c?.extKeyUsage?.clientAuth)
                  ? "True"
                  : "False"}
              </Typography>
            </DataPoint>
          </Container>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default DetailViewDialog;
